<template>
    <div 
        class="home-container"
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <program-info-home v-if="getEnableProgramInfo"/>
        <div class="home-container__lines">
            <Lines-home 
                ref="linesHome" 
                :enable-program-info="getEnableProgramInfo"
                @ready="readyLines"
                @show-all="showAllItems"
                @list-item-selected="itemsSelected"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
    import { linkItems } from "@/components/Header/types";
    import { PlayItem, type ProgramGuideItem } from "@ca.vmedia.tv/domains";
    import { useApplicationStore } from "@/stores/Application";
    import type { ChannelLiveCardData, ProgramCardData, OnDemandCardData } from "@/components/Cards/types";
    import { Logger} from '@/utils';
    import type { LinesHomeRef } from "@/components/widgets/LinesHome/types";
    import { useFocus } from "@/composables/useFocus";
    import  { 
        type NavigationGuardNext, 
        type RouteLocationNormalizedGeneric, 
        type RouteLocationNormalizedLoadedGeneric, 
        useRouter,
        onBeforeRouteLeave 
    } from "vue-router";
    import LinesHome from "@/components/widgets/LinesHome/LinesHome.vue";
    import { storeToRefs } from 'pinia'
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { usePlayerStore } from "@/stores/Player";
    import { useHomePageStore } from "@/stores/HomePage";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { AppConstants } from "@/common";
    import type {IEventSelected } from "@/components/RowView/types";
    import { Strings } from "@/locales/types/enums/strings";
    import { ScreenName } from "@/common/types/screen-names";


    const DetailPageStoreModule = useDetailPageStore();
    const {
        setViewedDetailItems,
        setCurrentItemDetail,
        clearAllDetailPage,
    } = DetailPageStoreModule;

    const {
        fetchEpisodes,
        getEnableProgramInfo
    } = useHomePageStore();

    const ClientMessagesStore = useClientMessagesStore();
    const {
        isModalVisible: isModalMessagesShown,
    } = storeToRefs(ClientMessagesStore);


    const ApplicationStore = useApplicationStore();
    const {
        getIsOktaServerErrorModal: isOktaServerErrorModal,
        getIsOktaTokenErrorModal: isOktaTokenErrorModal,
        getIsModalExitAppShown: isModalExitAppShown,
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        openModalExitApp,
        showPreviewSignInModal, 
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { setIsHaveFocusedElement, focusElementLine, focusToHeader } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);
    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingChannel,
        startPlayingProgram,
    } = PlayerStoreModule;

    definePageMeta({
        middleware: ['set-page-indicator', 'fetch-home'],
    });

    const { t } = useI18n();
    const { $fire } = useNuxtApp();
    const traceHomeScreen = $fire.trace($fire.performance, 'HOME_SCREEN');
    try {
        traceHomeScreen.start();
        Logger.captureLog('HOME_SCREEN start');
    } catch (e){
        Logger.captureLog(e);
    }

    const { shelfKeys }: typeof AppConstants = AppConstants;
    const router = useRouter();
    const { $navigator, $eventBus, $analytics } = useNuxtApp();
    const selectedProgram: Ref<ProgramGuideItem | null> = ref(null);
    const isReadyLines: Ref<boolean> = ref(false);
    const linesHome: Ref<LinesHomeRef | null> = ref(null);
    const isRegKeyListeners: Ref<boolean> = ref(false);

    const isLoadedComponents = computed((): boolean => linesHome.value !== null);
    const isReadyAll = computed((): boolean => isReadyLines.value && isLoadedComponents.value);

    const readyLines = (): void => {
        isReadyLines.value = true;
    };

    const itemsSelected = (val: IEventSelected) => {
        switch (val.lineId) {
            case shelfKeys.recentChannelsCards:
                onPlayChannel(val.item as unknown as ChannelLiveCardData);
                break;

            case shelfKeys.continueWatchingCards:
                onPlayOnDemandCardHome(val.item as unknown as OnDemandCardData);
                break;

            case shelfKeys.recentlyOnDemandCards:
                onPlayOnDemandCardHome(val.item as unknown as OnDemandCardData);
                break;

            case shelfKeys.watchAgainCards:
                onPlayOnDemandCardHome(val.item as unknown as OnDemandCardData);
                break;
                
            case shelfKeys.trendsCards:
                onPlayTrends(val.item as unknown as ProgramCardData);
                break;

            default:
                onPlayProgram(val.item as unknown as ProgramCardData);
                break;
        }
    };
    const onPlayChannel = (item: ChannelLiveCardData) => {
        unregisterKeyListeners();
        void startPlayingChannel(item);
    };
    const onPlayOnDemandCardHome = (item: OnDemandCardData) => {
        if (!isGuest.value) {
            try {
                const playItem = item.getPlayItem();
                if (!playItem) {
                    throw new Error("playItem is null:" + JSON.stringify(item));
                }
                setViewedDetailItems(playItem);
                if (PlayItem.isSeries(playItem)) {
                    void fetchEpisodes(playItem);
                }

                if (!PlayItem.isSeries(playItem)) {
                    setCurrentItemDetail(item.getId());
                }

                startPlayingProgram(playItem);
                void router.push(linkItems.player.routerPath);
            } catch (error) {
                Logger.captureError(error as Error);
                setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
                setTooltipeShowing({visible: true});
            }
        } else {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
        }
    };
    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyListeners.value = false;
        }
    };
    const keyBackListener = () => {
        focusToHeader();
    };
    const onPlayProgram = (item: ProgramCardData) => {
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }

        const program = item.getProgram();

        if (!program) {
            return;
        }
        selectedProgram.value = program;

        if (PlayItem.isLive(program)) {
            startPlayingProgram(program);
            unregisterKeyListeners();
            void router.push(linkItems.player.routerPath);
        } else {
            goToDetailsPageHome();
        }
    };
    const onPlayTrends = (item: ProgramCardData) => {
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }

        const program = item.getProgram();

        if (!program) {
            return;
        }
        selectedProgram.value = program;

        if (PlayItem.isLive(program)) {
            startPlayingProgram(program);
            unregisterKeyListeners();
            void router.push(linkItems.player.routerPath);
        } else {
            goToTrendsDetailsPageHome();
        }
    };
    const goToTrendsDetailsPageHome = () => {
        try {
            if (!selectedProgram.value) {
                throw new Error("Program is not selected");
            }
            if (selectedProgram.value.recentAirTime) {
                goToDetailsPageHome();
            } else  {
                setViewedDetailItems(selectedProgram.value);
                const path = PlayItem.isSeries(selectedProgram.value) ?
                    `${linkItems.detailsVodSeries.routerPath}/${selectedProgram.value.id}` :
                    `${linkItems.detailsVodMovies.routerPath}/${selectedProgram.value.id}`;
                void router.push(path);
            }  
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const goToDetailsPageHome = () => {
        try {
            if (!selectedProgram.value) {
                throw new Error("Program is not selected");
            }
            setViewedDetailItems(selectedProgram.value);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${selectedProgram.value.id}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const focusElement = () => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            focusToHeader();
        }
    };
    const focusFirstRow = () => {
        if (linesHome.value) {
            linesHome.value.focusFirstRow();
        }
    };
    const showAllItems = (val: IEventSelected) => {
        let _path: string;
        switch (val.lineId) {
            case shelfKeys.moviesCards:
                _path = linkItems.showAllHomeMovies.routerPath;
                break;
            case shelfKeys.sportsCards:
                _path = linkItems.showAllHomeSports.routerPath;
                break;
            case shelfKeys.kidsCards:
                _path = linkItems.showAllHomeKids.routerPath;
                break;
            case shelfKeys.newsCards:
                _path = linkItems.showAllHomeNews.routerPath;
                break;
            case shelfKeys.continueWatchingCards:
                _path = linkItems.showAllHomeContinueWatching.routerPath;
                break;
            case shelfKeys.watchAgainCards:
                _path = linkItems.showAllHomeWatchAgain.routerPath;
                break;
            case shelfKeys.trendsCards:
                _path = linkItems.showAllHomeTrends.routerPath;
                break;
            case shelfKeys.recentlyOnDemandCards:
                _path = linkItems.showAllHomeRecentlyOnDemand.routerPath;
                break;
            case shelfKeys.olympicsCards:
                _path = linkItems.showAllHomeOlympics.routerPath;
                break;
            case shelfKeys.liveSeries:
                _path = linkItems.showAllHomeLiveSeries.routerPath;
                break;
            default:
                throw new Error(`Unknown lineId: ${val.lineId}`);
        }
        void router.push(_path);
        unregisterKeyListeners();
    };

    const handlerReady = (): void => {
       void nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value && !isModalExitAppShown.value) {
                focusElement();
            }
        });

        if (traceHomeScreen) {
            try {
                traceHomeScreen.stop();
                Logger.captureLog(`HOME_SCREEN stop ${traceHomeScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    };

    onMounted((): void => {
       void nextTick((): void => {
            clearAllDetailPage();
            $analytics.createScreenEvent(ScreenName.HOME, ScreenName.HOME);

            $eventBus.on("leave-header", focusFirstRow);
            $eventBus.on("key-up-rows", focusToHeader);
        });
    });

    onUnmounted((): void => {
        $eventBus.off("leave-header", focusFirstRow);
        $eventBus.off("key-up-rows", focusToHeader);
    });

    onBeforeRouteLeave((to: RouteLocationNormalizedGeneric, _from: RouteLocationNormalizedLoadedGeneric, next: NavigationGuardNext): void => {
        if (to.path === linkItems.root.routerPath || (to.fullPath === linkItems.login.routerPath && !isGuest.value)) {
            openModalExitApp();
            next(false);
        } else {
            next();
        }
    });

    watch(isReadyAll, (value: boolean) => {
        if (value && !isOktaTokenErrorModal.value && !isOktaServerErrorModal.value && !isModalMessagesShown.value) {
            handlerReady();
        }
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>